import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createView,
  deleteView,
  listViews,
  setDefault,
  unsetDefault,
  updateView,
} from "@/api/views";
import type {
  SavedViewCreate,
  SavedViewEntity,
  SavedViewRead,
  SavedViewUpdate,
} from "@/api/types";

export interface UseViewsResult {
  views: SavedViewRead[];
  isLoading: boolean;
  error: string | null;
  /** Reload from the server. */
  reload: () => Promise<void>;
  /** The first view marked is_default for this entity, if any. */
  defaultView: SavedViewRead | null;
  /** True after the first fetch completes. Useful to gate "apply default on
   *  first load" effects so they don't fire before views are known. */
  loaded: boolean;
  // Mutations:
  create: (data: Omit<SavedViewCreate, "entity">) => Promise<SavedViewRead>;
  rename: (id: number, name: string) => Promise<SavedViewRead>;
  setFilters: (
    id: number,
    filters: Record<string, unknown>,
  ) => Promise<SavedViewRead>;
  setShared: (id: number, isShared: boolean) => Promise<SavedViewRead>;
  patch: (id: number, data: SavedViewUpdate) => Promise<SavedViewRead>;
  remove: (id: number) => Promise<void>;
  toggleDefault: (id: number, current: boolean) => Promise<SavedViewRead>;
}

/**
 * Loads the saved views scoped to one entity (e.g. companies_prospects)
 * within a project, and exposes a small mutation API.
 *
 * View ownership is enforced by the backend; the hook just routes requests.
 */
export function useViews(
  slug: string | null,
  entity: SavedViewEntity | null,
): UseViewsResult {
  const [views, setViews] = useState<SavedViewRead[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the latest (slug, entity) so a stale fetch's response doesn't
  // overwrite a newer one when the user switches entities (e.g. tabs).
  const latestKeyRef = useRef<string>("");

  const reload = useCallback(async () => {
    if (!slug || !entity) {
      setViews([]);
      setLoaded(true);
      return;
    }
    const key = `${slug}::${entity}`;
    latestKeyRef.current = key;
    setLoading(true);
    setError(null);
    try {
      const res = await listViews(slug, entity);
      if (latestKeyRef.current !== key) return;
      setViews(res);
    } catch (err) {
      if (latestKeyRef.current !== key) return;
      setError(extractMessage(err));
      setViews([]);
    } finally {
      if (latestKeyRef.current === key) {
        setLoading(false);
        setLoaded(true);
      }
    }
  }, [slug, entity]);

  useEffect(() => {
    setLoaded(false);
    void reload();
  }, [reload]);

  const defaultView = useMemo(
    () => views.find((v) => v.is_default) ?? null,
    [views],
  );

  // ─── Mutations (all keep local state in sync) ────────────────────────────

  const replaceLocal = useCallback((view: SavedViewRead) => {
    setViews((prev) => {
      const idx = prev.findIndex((v) => v.id === view.id);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = view;
        return next;
      }
      return [...prev, view];
    });
  }, []);

  const create = useCallback(
    async (data: Omit<SavedViewCreate, "entity">) => {
      if (!slug || !entity) throw new Error("Project / entity not ready");
      const created = await createView(slug, { ...data, entity });
      replaceLocal(created);
      return created;
    },
    [slug, entity, replaceLocal],
  );

  const patch = useCallback(
    async (id: number, data: SavedViewUpdate) => {
      if (!slug) throw new Error("Project not ready");
      const updated = await updateView(slug, id, data);
      replaceLocal(updated);
      return updated;
    },
    [slug, replaceLocal],
  );

  const rename = useCallback(
    (id: number, name: string) => patch(id, { name }),
    [patch],
  );

  const setFilters = useCallback(
    (id: number, filters: Record<string, unknown>) => patch(id, { filters }),
    [patch],
  );

  const setShared = useCallback(
    (id: number, isShared: boolean) =>
      patch(id, { is_shared: isShared }),
    [patch],
  );

  const remove = useCallback(
    async (id: number) => {
      if (!slug) throw new Error("Project not ready");
      await deleteView(slug, id);
      setViews((prev) => prev.filter((v) => v.id !== id));
    },
    [slug],
  );

  const toggleDefault = useCallback(
    async (id: number, current: boolean) => {
      if (!slug) throw new Error("Project not ready");
      const updated = current
        ? await unsetDefault(slug, id)
        : await setDefault(slug, id);
      // Setting one default unsets the others on the server. Reload to keep
      // local state honest about everyone's is_default.
      await reload();
      return updated;
    },
    [slug, reload],
  );

  return {
    views,
    isLoading,
    error,
    reload,
    defaultView,
    loaded,
    create,
    rename,
    setFilters,
    setShared,
    patch,
    remove,
    toggleDefault,
  };
}

function extractMessage(err: unknown): string {
  if (err && typeof err === "object" && "message" in err) {
    return String((err as { message?: unknown }).message);
  }
  return "Couldn't load views.";
}

/**
 * Compare two filter records by canonical JSON. Treats `null`, `undefined`,
 * empty arrays/objects, and empty strings as equal (so "no filter" round-trips
 * cleanly). Used by pages to detect when the active view is still the saved
 * one vs. the user has edited filters since picking it.
 */
export function filtersEqual(
  a: Record<string, unknown> | null | undefined,
  b: Record<string, unknown> | null | undefined,
): boolean {
  return canonical(a) === canonical(b);
}

function canonical(v: unknown): string {
  if (v === null || v === undefined) return "null";
  if (typeof v === "string") return v === "" ? "null" : JSON.stringify(v);
  if (typeof v === "number" || typeof v === "boolean") return JSON.stringify(v);
  if (Array.isArray(v)) {
    if (v.length === 0) return "null";
    return "[" + v.map(canonical).join(",") + "]";
  }
  if (typeof v === "object") {
    const obj = v as Record<string, unknown>;
    const entries = Object.keys(obj)
      .sort()
      .map((k) => [k, canonical(obj[k])] as const)
      .filter(([, val]) => val !== "null");
    if (entries.length === 0) return "null";
    return (
      "{" +
      entries.map(([k, val]) => JSON.stringify(k) + ":" + val).join(",") +
      "}"
    );
  }
  return "null";
}
